/*!
 * FPU Marquee — Endlos-Laufband (Logo-Ticker) als reine CSS-Animation mit
 * JS-Vermessung. FlowPowerUser Attribute, Schema: fpu-marquee-*
 * Version: 1.0.0
 *
 * Attribute:
 *   fpu-marquee-element="wrapper"   Aktivierung; sichtbarer Ausschnitt
 *                                        (overflow:hidden wird gesetzt)
 *   fpu-marquee-element="track"     Inhalts-Element im Wrapper; seine
 *                                        Kinder bilden eine Wiederholung
 *   fpu-marquee-element="pause"     optionaler Pause/Play-Umschalter
 *                                        (aria-pressed, WCAG 2.2.2); im Wrapper
 *                                        liegend oder außerhalb via -instance
 *                                        verknüpft — nicht IN den Track legen
 *   fpu-marquee-instance="n"        verbindet externe pause-Elemente mit
 *                                        dem Wrapper gleicher Instanz-Nummer
 *   fpu-marquee-speed="60"          Geschwindigkeit in px/s (Default 60);
 *                                        die Dauer wird aus der gemessenen
 *                                        Content-Breite berechnet und bleibt
 *                                        damit konstant über alle Breakpoints
 *   fpu-marquee-direction="left"    left | right | up | down (Default
 *                                        left); up/down messen Höhe statt
 *                                        Breite — der Wrapper braucht dann
 *                                        eine begrenzte Höhe
 *   fpu-marquee-pause-hover="true"  pausiert bei Hover/Fokus im Wrapper
 *   fpu-marquee-gap="48"            zusätzlicher Abstand zwischen den
 *                                        Wiederholungen in px (zusätzlich zu
 *                                        einem evtl. vorhandenen Track-Gap)
 *   fpu-marquee-fade="true"         Fade-Masken an den Rändern (48px);
 *                                        numerischer Wert = eigene Breite in px
 *   fpu-marquee-drag="true"         Drag-Modus: Laufband per Maus/Touch
 *                                        greifbar (1:1-Scrub ab 5px-Schwelle,
 *                                        Klicks auf Logo-Links bleiben intakt),
 *                                        Wurf rollt mit Momentum aus und
 *                                        blendet sanft zurück in den Basis-
 *                                        Lauf; Bewegung läuft dann über rAF
 *                                        statt über die CSS-Keyframe-Animation
 *   fpu-marquee-logging="true"      eine Init-Zeile in der Konsole
 *
 * Globale Defaults (optional, Attribut gewinnt immer):
 *   window.fpuMarqueeConfig = { speed, direction, pauseHover, gap, fade, drag, logging }
 *
 * API: window.fpuMarquee = { instances, init, destroyAll }
 *
 * Architektur: Die Original-Kinder des Tracks werden in eine Gruppe VERSCHOBEN
 * (appendChild, kein cloneNode auf Originalen — Webflow-IX2 bleibt intakt);
 * nur die Gruppe wird geklont, bis der Track ≥ 2× Wrapper-Größe füllt
 * (ResizeObserver-reaktiv; Klone aria-hidden, ohne IDs, Fokusziele mit
 * tabindex="-1"). Die Bewegung läuft als reine CSS-Keyframe-Animation auf
 * transform (GPU/Compositor, kein JS im Hot Path) über exakt eine gemessene
 * Wiederholungs-Distanz via CSS-Variable — dadurch nahtloser Loop und
 * px/s-konstante Geschwindigkeit. prefers-reduced-motion stoppt rein per CSS:
 * statisch, Inhalt bricht um, nichts wird abgeschnitten. Dispatcht keine Events.
 *
 * Drag-Modus (fpu-marquee-drag="true"): Mess- und Klon-Logik bleiben
 * identisch, aber die Bewegung läuft über einen rAF-Loop mit virtueller
 * Position und translate3d auf dem Track — nahtloser Wrap in BEIDE Richtungen
 * über die gemessene Perioden-Distanz (vorzeichen-sicheres Modulo). Pointer-
 * Drag mit 5px-Schwelle (darunter bleibt es ein Klick), window-Listener pro
 * Drag via eigenem AbortController, Klick-Unterdrückung 300ms nach echtem
 * Drag. Release: Wurf-Velocity rollt exponentiell aus und blendet über ~0.5s
 * zurück in Basis-Richtung und -Geschwindigkeit (Wurf entgegen der
 * Laufrichtung kehrt sanft um). Hover-Pause und Pause-Button stoppen nur den
 * Auto-Vortrieb — Drag bleibt möglich; im pausierten Zustand rollt ein Wurf
 * aus und bleibt stehen. prefers-reduced-motion: kein Auto-Lauf, Drag erlaubt
 * (nutzerinitiiert), Release ohne Momentum. Der Loop pausiert bei
 * document.hidden und außerhalb des Viewports (IntersectionObserver).
 * Klassen: fpu-marquee-drag (Wrapper, cursor grab + touch-action) und
 * fpu-marquee-dragging (während des Ziehens, grabbing + user-select none).
 *
 * Hinweise: Direktkind-Selektoren auf dem Track (.track > *) treffen nach dem
 * Init die Gruppe statt der Items. Bei verschachtelten Marquees pausiert ein
 * äußeres Pause auch die inneren (bewusst, Kaskade).
 */
(() => {
  'use strict';

  const WRAPPER_SELECTOR = '[fpu-marquee-element="wrapper"]';
  const TRACK_SELECTOR = '[fpu-marquee-element="track"]';
  const PAUSE_SELECTOR = '[fpu-marquee-element="pause"]';
  const STYLE_ID = 'fpu-marquee-styles';
  const LOG_PREFIX = '[FPU Marquee]';
  const VERSION = '1.0.0';

  const DEFAULT_SPEED = 60;  // px/s
  const DEFAULT_FADE = 48;   // px Fade-Breite bei -fade="true"
  const MAX_GROUPS = 80;     // Sicherheitsdeckel (z. B. vertikal ohne begrenzte Wrapper-Höhe)
  const DIRECTIONS = ['left', 'right', 'up', 'down'];

  // Drag-Modus
  const DRAG_THRESHOLD = 5;       // px Pointer-Weg, ab dem ein Drag beginnt (darunter: Klick)
  const CLICK_SUPPRESS_MS = 300;  // ms Klick-Unterdrückung nach einem echten Drag
  const MAX_FRAME_DT = 0.05;      // s Obergrenze pro Frame — kappt Sprünge nach Tab-Rückkehr
  const DECAY_PER_FRAME = 0.95;   // Wurf-Ausrollen pro 60fps-Frame (framerate-normalisiert)
  const BLEND_MS = 500;           // ms Überblendung zurück in die Basis-Geschwindigkeit
  const STOP_EPS = 4;             // px/s — darunter gilt das Ausrollen als beendet
  const MAX_FLING = 6000;         // px/s Deckel für die Wurf-Geschwindigkeit
  const STALE_SAMPLE_MS = 80;     // ms — älteres Velocity-Sample beim Loslassen verwerfen

  let GLOBAL = {}; // wird in initAll() gelesen — Config darf nach dem Script stehen
  const instances = [];
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const fadeX = 'linear-gradient(to right, transparent, #000 var(--fpu-marquee-fade-size, 48px), #000 calc(100% - var(--fpu-marquee-fade-size, 48px)), transparent)';
    const fadeY = 'linear-gradient(to bottom, transparent, #000 var(--fpu-marquee-fade-size, 48px), #000 calc(100% - var(--fpu-marquee-fade-size, 48px)), transparent)';
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.fpu-marquee-wrapper { overflow: hidden; }',
      '.fpu-marquee-track { display: flex; flex-wrap: nowrap; }',
      '.fpu-marquee-track-x { width: max-content; max-width: none; }',
      '.fpu-marquee-track-y { flex-direction: column; height: max-content; max-height: none; }',
      // Die Gruppe spiegelt das Track-Layout: "inherit" bezieht sich auf den
      // Track als Eltern-Element — Autoren-Styles (flex, gap, align) wirken
      // damit unverändert auf die Items innerhalb jeder Wiederholung.
      '.fpu-marquee-group { display: inherit; flex-direction: inherit; flex-wrap: nowrap; gap: inherit; align-items: inherit; justify-content: inherit; flex-shrink: 0; }',
      '.fpu-marquee-track-x > .fpu-marquee-group { margin-inline-end: var(--fpu-marquee-gap, 0px); }',
      '.fpu-marquee-track-y > .fpu-marquee-group { margin-block-end: var(--fpu-marquee-gap, 0px); }',
      // Animation erst nach erfolgreicher Vermessung (fpu-marquee-ready) —
      // verhindert eine 0s-Endlosanimation vor dem ersten sync().
      '.fpu-marquee-track-x.fpu-marquee-ready { animation: fpu-marquee-x var(--fpu-marquee-duration, 20s) linear infinite; will-change: transform; }',
      '.fpu-marquee-track-y.fpu-marquee-ready { animation: fpu-marquee-y var(--fpu-marquee-duration, 20s) linear infinite; will-change: transform; }',
      // Nach den Shorthand-Regeln platziert (Shorthand setzt direction/play-state zurück)
      '.fpu-marquee-track.fpu-marquee-track-reverse { animation-direction: reverse; }',
      '.fpu-marquee-hover-pause:hover .fpu-marquee-track, .fpu-marquee-hover-pause:focus-within .fpu-marquee-track { animation-play-state: paused; }',
      '.fpu-marquee-paused .fpu-marquee-track { animation-play-state: paused !important; }',
      `.fpu-marquee-fade.fpu-marquee-horizontal { -webkit-mask-image: ${fadeX}; mask-image: ${fadeX}; }`,
      `.fpu-marquee-fade.fpu-marquee-vertical { -webkit-mask-image: ${fadeY}; mask-image: ${fadeY}; }`,
      // Drag-Modus: Bewegung kommt aus JS (translate3d auf dem Track) — die
      // Keyframe-Animation wird neutralisiert. touch-action lässt dem Browser
      // die Querachse (Seiten-Scroll bleibt auf Touch möglich).
      '.fpu-marquee-track.fpu-marquee-drag-mode { animation: none !important; will-change: transform; }',
      '.fpu-marquee-drag { cursor: grab; }',
      '.fpu-marquee-drag.fpu-marquee-horizontal { touch-action: pan-y; }',
      '.fpu-marquee-drag.fpu-marquee-vertical { touch-action: pan-x; }',
      '.fpu-marquee-drag.fpu-marquee-dragging { cursor: grabbing; user-select: none; -webkit-user-select: none; }',
      '@keyframes fpu-marquee-x { from { transform: translate3d(0, 0, 0); } to { transform: translate3d(calc(var(--fpu-marquee-distance, 0px) * -1), 0, 0); } }',
      '@keyframes fpu-marquee-y { from { transform: translate3d(0, 0, 0); } to { transform: translate3d(0, calc(var(--fpu-marquee-distance, 0px) * -1), 0); } }',
      // prefers-reduced-motion: steht still, Klone verschwinden, Inhalt bricht
      // um bzw. bleibt scrollbar erreichbar — kein Overflow-Schnitt (WCAG 2.2.2/2.3.3).
      '@media (prefers-reduced-motion: reduce) {',
      '  .fpu-marquee-wrapper { overflow: auto; -webkit-mask-image: none !important; mask-image: none !important; }',
      '  .fpu-marquee-track { animation: none !important; width: auto !important; height: auto !important; }',
      '  .fpu-marquee-group { flex-wrap: wrap; margin: 0 !important; }',
      '  .fpu-marquee-clone { display: none !important; }',
      '  .fpu-marquee-pause-control { display: none !important; }',
      // Drag-Modus unter reduced-motion: Layout bleibt Marquee (Drag ist
      // nutzerinitiiert und damit erlaubt) — nur der Auto-Lauf entfällt,
      // das regelt die JS-Engine. Fade-Masken bleiben bewusst entfernt.
      '  .fpu-marquee-drag.fpu-marquee-wrapper { overflow: hidden; }',
      '  .fpu-marquee-drag .fpu-marquee-track-x { width: max-content !important; }',
      '  .fpu-marquee-drag .fpu-marquee-track-y { height: max-content !important; }',
      '  .fpu-marquee-drag .fpu-marquee-group { flex-wrap: nowrap; }',
      '  .fpu-marquee-drag .fpu-marquee-track-x > .fpu-marquee-group { margin-inline-end: var(--fpu-marquee-gap, 0px) !important; }',
      '  .fpu-marquee-drag .fpu-marquee-track-y > .fpu-marquee-group { margin-block-end: var(--fpu-marquee-gap, 0px) !important; }',
      '  .fpu-marquee-drag .fpu-marquee-clone { display: inherit !important; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  class FpuMarquee {
    constructor(wrapper, track, id) {
      this.wrapper = wrapper;
      this.track = track;
      this.id = id;
      this.instanceKey = wrapper.getAttribute('fpu-marquee-instance');
      this.controller = new AbortController();
      this.pauseControls = [];
      this.paused = false;
      this.rafId = 0;
      this.lastUnit = 0;
      this.capWarned = false;

      // Drag-Engine-Zustand (nur im Drag-Modus genutzt)
      this.pos = 0;              // virtuelle Position in px entlang der Laufachse
      this.vel = 0;              // aktuelle Geschwindigkeit in px/s (vorzeichenbehaftet)
      this.phase = 'auto';       // 'auto' | 'decay' (Ausrollen) | 'blend' (Überblendung)
      this.blendFrom = 0;
      this.blendStart = 0;
      this.engineRafId = 0;
      this.lastFrameTime = 0;
      this.dragging = false;
      this.dragController = null;
      this.pointerId = null;
      this.startCoord = 0;
      this.lastCoord = 0;
      this.lastMoveTime = 0;
      this.dragStartPos = 0;
      this.sampleVel = 0;
      this.suppressClicksUntil = 0;
      this.hoverPaused = false;  // Maus über dem Wrapper (Pendant zu :hover)
      this.focusPaused = false;  // Fokus im Wrapper (Pendant zu :focus-within)
      this.inView = true;
      this.viewObserver = null;

      // Optionen: Attribut → globale Config → Default
      this.speed = this.readNumber('fpu-marquee-speed', GLOBAL.speed, DEFAULT_SPEED);
      this.direction = this.readDirection();
      this.horizontal = this.direction === 'left' || this.direction === 'right';
      this.reverse = this.direction === 'right' || this.direction === 'down';
      this.pauseHover = this.readBool('fpu-marquee-pause-hover', GLOBAL.pauseHover, false);
      this.drag = this.readBool('fpu-marquee-drag', GLOBAL.drag, false);
      this.logging = this.readBool('fpu-marquee-logging', GLOBAL.logging, false);
      this.gap = this.readNumber('fpu-marquee-gap', GLOBAL.gap, 0);
      this.fadeSize = this.readFade();

      // Original-Kinder in eine Gruppe VERSCHIEBEN (kein Klonen der Originale —
      // Webflow-Interactions auf den Items bleiben funktionsfähig).
      const group = document.createElement('div');
      group.className = 'fpu-marquee-group';
      while (track.firstChild) group.appendChild(track.firstChild);
      track.appendChild(group);

      // Mindestens eine Kopie, damit die Wiederholungs-Distanz messbar ist
      // (Abstand zwischen den ersten beiden Gruppen inkl. Gap/Margins).
      const firstClone = this.createClone(group);
      track.appendChild(firstClone);
      this.groups = [group, firstClone];

      // Klassen + CSS-Variablen
      wrapper.classList.add('fpu-marquee-wrapper', this.horizontal ? 'fpu-marquee-horizontal' : 'fpu-marquee-vertical');
      track.classList.add('fpu-marquee-track', this.horizontal ? 'fpu-marquee-track-x' : 'fpu-marquee-track-y');
      if (this.reverse) track.classList.add('fpu-marquee-track-reverse');
      if (this.pauseHover) wrapper.classList.add('fpu-marquee-hover-pause');
      if (this.gap > 0) wrapper.style.setProperty('--fpu-marquee-gap', `${this.gap}px`);
      if (this.fadeSize > 0) {
        wrapper.classList.add('fpu-marquee-fade');
        if (this.fadeSize !== DEFAULT_FADE) wrapper.style.setProperty('--fpu-marquee-fade-size', `${this.fadeSize}px`);
      }

      // Drag-Modus: Engine-Umschaltung (rAF statt CSS-Keyframes) + Gesten
      if (this.drag) {
        wrapper.classList.add('fpu-marquee-drag');
        track.classList.add('fpu-marquee-drag-mode');
        this.setupDrag();
      }

      this.sync();

      // Reagiert auf Breakpoint-Wechsel (Wrapper) und Content-Änderungen wie
      // nachladende Bilder oder Webfonts (Gruppe).
      this.resizeObserver = new ResizeObserver(() => this.scheduleSync());
      this.resizeObserver.observe(wrapper);
      this.resizeObserver.observe(group);

      // Live-Umschalten der Systemeinstellung: zurück zu Bewegung → neu
      // vermessen (beim Init unter reduced-motion lief noch kein sync durch).
      REDUCED_MOTION.addEventListener('change', () => this.scheduleSync(), { signal: this.controller.signal });

      if (this.logging) {
        console.info(`${LOG_PREFIX} Marquee #${id}: direction=${this.direction}, speed=${this.speed}px/s, ${this.groups.length - 1} Klon(e), pauseHover=${this.pauseHover}, drag=${this.drag}, fade=${this.fadeSize > 0}`);
      }
    }

    // ── Options-Helfer ───────────────────────────────────────────────────────

    readBool(attr, globalValue, fallback) {
      const v = this.wrapper.getAttribute(attr);
      if (v !== null) return v === 'true';
      if (typeof globalValue === 'boolean') return globalValue;
      return fallback;
    }

    readNumber(attr, globalValue, fallback) {
      const v = parseFloat(this.wrapper.getAttribute(attr));
      if (Number.isFinite(v) && v > 0) return v;
      if (typeof globalValue === 'number' && Number.isFinite(globalValue) && globalValue > 0) return globalValue;
      return fallback;
    }

    readDirection() {
      const v = this.wrapper.getAttribute('fpu-marquee-direction');
      if (DIRECTIONS.includes(v)) return v;
      if (v !== null) {
        console.warn(`${LOG_PREFIX} Marquee #${this.id}: unbekannte direction "${v}" — Fallback auf "left".`);
      }
      if (DIRECTIONS.includes(GLOBAL.direction)) return GLOBAL.direction;
      return 'left';
    }

    readFade() {
      const v = this.wrapper.getAttribute('fpu-marquee-fade');
      if (v !== null) {
        if (v === 'true') return DEFAULT_FADE;
        const n = parseFloat(v);
        return Number.isFinite(n) && n > 0 ? n : 0;
      }
      if (GLOBAL.fade === true) return DEFAULT_FADE;
      if (typeof GLOBAL.fade === 'number' && GLOBAL.fade > 0) return GLOBAL.fade;
      return 0;
    }

    // ── Klone ────────────────────────────────────────────────────────────────

    createClone(source) {
      const clone = source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('fpu-marquee-clone');
      // Duplikat-IDs entschärfen (Anker- und Label-Bezüge müssen eindeutig bleiben)
      if (clone.id) clone.removeAttribute('id');
      for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
      // Tastatur-Falle vermeiden: Klone sind rein dekorativ, Maus-Klicks auf
      // Links funktionieren weiterhin (bewusst kein inert).
      for (const el of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
        el.setAttribute('tabindex', '-1');
      }
      return clone;
    }

    // ── Vermessung & Synchronisation ─────────────────────────────────────────

    scheduleSync() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.sync();
      });
    }

    // Misst die exakte Wiederholungs-Distanz (subpixelgenau, transform-immun,
    // da beide Gruppen identisch verschoben sind), klont bedarfsgerecht nach
    // oder zurück und schreibt Distanz + Dauer als CSS-Variablen. Läuft nur
    // bei Init/Resize — die Bewegung selbst ist reine CSS-Animation.
    sync() {
      // Unter reduced-motion sind die Klon-Gruppen display:none — ihre Rects
      // wären 0 und die Messung würde falsche Variablen schreiben. CSS zeigt
      // ohnehin den statischen Zustand; der change-Listener vermisst beim
      // Zurückschalten neu. Ausnahme Drag-Modus: dort bleibt das Layout per
      // CSS-Ausnahme intakt (Drag ist nutzerinitiiert erlaubt) und wird
      // daher normal vermessen.
      if (REDUCED_MOTION.matches && !this.drag) return;
      const wrapperSize = this.horizontal ? this.wrapper.clientWidth : this.wrapper.clientHeight;
      const a = this.groups[0].getBoundingClientRect();
      const b = this.groups[1].getBoundingClientRect();
      const unit = this.horizontal ? Math.abs(b.left - a.left) : Math.abs(b.top - a.top);
      const groupSize = this.horizontal ? a.width : a.height;
      if (!wrapperSize || unit < 0.5 || groupSize < 0.5) return; // unsichtbar/leer → nächster Resize versucht es erneut

      // Gruppenbedarf: Gesamtlänge ≥ 2× Wrapper UND nach einer vollen Periode
      // noch lückenlos bedeckt (relevant, wenn eine Wiederholung breiter als
      // der Wrapper ist). Gesamtlänge = (n-1)·unit + groupSize.
      let needed = Math.max(
        2,
        Math.ceil((wrapperSize * 2 - groupSize) / unit) + 1,
        Math.ceil((wrapperSize - groupSize) / unit) + 2
      );
      if (needed > MAX_GROUPS) {
        needed = MAX_GROUPS;
        if (!this.capWarned) {
          this.capWarned = true;
          console.warn(`${LOG_PREFIX} Marquee #${this.id}: Klon-Limit erreicht — hat der Wrapper eine begrenzte ${this.horizontal ? 'Breite' : 'Höhe'}?`);
        }
      }
      while (this.groups.length < needed) {
        const clone = this.createClone(this.groups[0]);
        this.track.appendChild(clone);
        this.groups.push(clone);
      }
      while (this.groups.length > needed) {
        this.groups.pop().remove();
      }

      // Nur bei echter Änderung schreiben — vermeidet unnötige Sprünge in der
      // laufenden Animation. Dauer = Distanz / Geschwindigkeit (px/s).
      if (Math.abs(unit - this.lastUnit) > 0.5) {
        // Drag-Modus: relative Phase erhalten — die virtuelle Position
        // skaliert mit der neuen Perioden-Distanz statt sichtbar zu springen.
        if (this.drag && this.lastUnit > 0) {
          this.pos = (this.pos / this.lastUnit) * unit;
          // Mitten im Scrub: Drag-Referenz auf die reskalierte Position
          // rebasen — sonst überschreibt der nächste pointermove die Position
          // wieder auf Basis der alten Distanz (sichtbarer Sprung).
          if (this.dragging) {
            this.dragStartPos = this.pos;
            this.startCoord = this.lastCoord;
          }
        }
        this.lastUnit = unit;
        this.wrapper.style.setProperty('--fpu-marquee-distance', `${unit.toFixed(3)}px`);
        this.wrapper.style.setProperty('--fpu-marquee-duration', `${(unit / this.speed).toFixed(3)}s`);
      }
      this.track.classList.add('fpu-marquee-ready');

      // Drag-Modus: Position anwenden und Engine (wieder) anwerfen —
      // z. B. nach dem ersten Vermessen oder einem Breakpoint-Wechsel.
      if (this.drag) {
        this.applyPos();
        this.startEngine();
      }
    }

    // ── Drag-Modus: rAF-Engine mit virtueller Position ───────────────────────

    setupDrag() {
      const { signal } = this.controller;

      // rAF nur bei sichtbarem Marquee — außerhalb des Viewports keine Arbeit.
      this.viewObserver = new IntersectionObserver((entries) => {
        this.inView = entries[entries.length - 1].isIntersecting;
        if (this.inView) {
          this.lastFrameTime = 0;
          this.startEngine();
        }
      });
      this.viewObserver.observe(this.wrapper);

      // Tab versteckt → laufendes rAF abbrechen (keine Hintergrund-Arbeit);
      // bei Rückkehr frisch starten — der lastFrameTime-Reset verhindert
      // zusätzlich zum dt-Cap jeden Zeitsprung.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          if (this.engineRafId) {
            cancelAnimationFrame(this.engineRafId);
            this.engineRafId = 0;
          }
        } else {
          this.lastFrameTime = 0;
          this.startEngine();
        }
      }, { signal });

      this.wrapper.addEventListener('pointerdown', (e) => this.handleDragDown(e), { signal });

      // Geister-Klicks nach einem echten Drag unterdrücken — Klicks ohne
      // Drag (unter der Schwelle) erreichen Logo-Links völlig normal.
      this.wrapper.addEventListener('click', (e) => {
        if (performance.now() < this.suppressClicksUntil) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { capture: true, signal });

      // Natives Bild-/Link-Dragging würde den Pointer-Drag kapern
      this.wrapper.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

      // Hover-/Fokus-Pause: im Drag-Modus gibt es keine CSS-Animation, deren
      // play-state pausieren könnte — daher JS-seitig nachgebildet
      // (:hover nur für Maus, sonst bliebe Touch dauerhaft "gehovert").
      if (this.pauseHover) {
        this.wrapper.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.hoverPaused = true;
        }, { signal });
        this.wrapper.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.hoverPaused = false;
          this.startEngine();
        }, { signal });
        this.wrapper.addEventListener('focusin', () => { this.focusPaused = true; }, { signal });
        this.wrapper.addEventListener('focusout', (e) => {
          if (e.relatedTarget && this.wrapper.contains(e.relatedTarget)) return;
          this.focusPaused = false;
          this.startEngine();
        }, { signal });
      }
    }

    handleDragDown(e) {
      if (this.pointerId !== null) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Formularelemente und der Pause-Button bleiben normal bedienbar
      if (e.target.closest('input, select, textarea, button, [contenteditable], .fpu-marquee-pause-control')) return;

      this.pointerId = e.pointerId;
      this.dragging = false;
      this.startCoord = this.lastCoord = this.horizontal ? e.clientX : e.clientY;
      this.lastMoveTime = e.timeStamp;
      this.sampleVel = 0;

      // Move/Up auf window: funktioniert auch nach Verlassen des Wrappers,
      // ohne setPointerCapture (das würde Klicks auf Logo-Links umlenken).
      this.dragController = new AbortController();
      const opts = { passive: true, signal: this.dragController.signal };
      window.addEventListener('pointermove', (ev) => this.handleDragMove(ev), opts);
      window.addEventListener('pointerup', (ev) => this.handleDragEnd(ev), opts);
      window.addEventListener('pointercancel', (ev) => this.handleDragEnd(ev), opts);
    }

    handleDragMove(e) {
      if (e.pointerId !== this.pointerId) return;
      const coord = this.horizontal ? e.clientX : e.clientY;
      const dt = e.timeStamp - this.lastMoveTime;
      if (dt > 0) this.sampleVel = ((coord - this.lastCoord) / dt) * 1000; // px/s
      this.lastCoord = coord;
      this.lastMoveTime = e.timeStamp;

      if (!this.dragging) {
        if (Math.abs(coord - this.startCoord) < DRAG_THRESHOLD) return;
        // Schwelle überschritten → ab jetzt 1:1-Scrub. Referenz neu setzen,
        // damit der Einstieg ohne 5px-Sprung beginnt.
        this.dragging = true;
        this.startCoord = coord;
        this.dragStartPos = this.pos;
        this.wrapper.classList.add('fpu-marquee-dragging');
        this.startEngine();
      }
      // Position setzen — angewendet wird sie im rAF-Tick (ein Write pro Frame)
      this.pos = this.wrapPos(this.dragStartPos + (coord - this.startCoord));
    }

    handleDragEnd(e) {
      if (e.pointerId !== this.pointerId) return;
      this.pointerId = null;
      if (this.dragController) {
        this.dragController.abort();
        this.dragController = null;
      }
      if (!this.dragging) return; // war nur ein Klick — nichts unterdrücken

      this.dragging = false;
      this.wrapper.classList.remove('fpu-marquee-dragging');
      this.suppressClicksUntil = performance.now() + CLICK_SUPPRESS_MS;
      this.applyPos();

      // Wurf-Geschwindigkeit = letztes Move-Sample; ruhte der Pointer vor dem
      // Loslassen oder hat der Browser die Geste übernommen (pointercancel,
      // z. B. vertikales Scrollen auf Touch), zählt 0.
      let v = e.timeStamp - this.lastMoveTime > STALE_SAMPLE_MS ? 0 : this.sampleVel;
      if (e.type === 'pointercancel') v = 0;
      v = Math.max(-MAX_FLING, Math.min(MAX_FLING, v));

      // reduced-motion: Drag ja (nutzerinitiiert), Momentum nein — sofort stehen.
      if (REDUCED_MOTION.matches) {
        this.vel = 0;
        this.phase = 'auto';
        return;
      }

      this.vel = v;
      if (Math.abs(v) > this.speed) {
        this.phase = 'decay'; // erst ausrollen (auch entgegen der Laufrichtung)
      } else if (this.autoDriveActive()) {
        this.startBlend(); // schon langsam genug → direkt sanft überblenden
      } else {
        // pausiert (Button/Hover): ausrollen und stehen bleiben
        this.phase = Math.abs(v) > STOP_EPS ? 'decay' : 'auto';
      }
      this.startEngine();
    }

    // Auto-Vortrieb erlaubt? Pause-Button und Hover-Pause stoppen nur den
    // automatischen Lauf — Drag selbst bleibt jederzeit möglich.
    autoDriveActive() {
      return !this.paused && !this.hoverPaused && !this.focusPaused && !REDUCED_MOTION.matches;
    }

    // Basis-Geschwindigkeit entlang der Achse: left/up = negativ, right/down = positiv
    baseVel() {
      return (this.reverse ? 1 : -1) * this.speed;
    }

    startBlend(now = performance.now()) {
      this.phase = 'blend';
      this.blendFrom = this.vel;
      this.blendStart = now;
    }

    // Vorzeichen-sicheres Modulo über die gemessene Perioden-Distanz: hält
    // die Position in (-unit, 0] — dort deckt der Track den Wrapper in beide
    // Richtungen lückenlos (gleiche Distanz wie die CSS-Keyframes).
    wrapPos(p) {
      const unit = this.lastUnit;
      if (!unit) return p;
      p %= unit;
      if (p > 0) p -= unit;
      return p;
    }

    applyPos() {
      const v = `${this.pos.toFixed(2)}px`;
      this.track.style.transform = this.horizontal ? `translate3d(${v}, 0, 0)` : `translate3d(0, ${v}, 0)`;
    }

    // Braucht die Engine gerade Frames? Sonst beendet sich der Loop selbst —
    // keine Arbeit bei Pause, Hover, verstecktem Tab oder außerhalb des Viewports.
    needsFrames() {
      if (!this.drag || !this.lastUnit) return false;
      if (this.dragging) return true;
      if (document.hidden || !this.inView) return false;
      if (this.phase === 'decay' || this.phase === 'blend') return true;
      return this.autoDriveActive();
    }

    startEngine() {
      if (!this.drag || this.engineRafId || !this.needsFrames()) return;
      this.lastFrameTime = 0;
      this.engineRafId = requestAnimationFrame((now) => this.engineTick(now));
    }

    engineTick(now) {
      this.engineRafId = 0;
      if (!this.lastFrameTime) this.lastFrameTime = now;
      // dt kappen: nach Tab-Rückkehr oder Ruckler keinen Riesensprung machen
      const dt = Math.min((now - this.lastFrameTime) / 1000, MAX_FRAME_DT);
      this.lastFrameTime = now;

      if (this.dragging) {
        // Position kommt 1:1 aus den Pointer-Move-Events — hier nur anwenden
      } else if (this.phase === 'decay') {
        // Exponentielles Ausrollen, framerate-normalisiert (0.95 pro 60fps-Frame)
        this.vel *= Math.pow(DECAY_PER_FRAME, dt * 60);
        this.pos = this.wrapPos(this.pos + this.vel * dt);
        const targetAbs = this.autoDriveActive() ? this.speed : 0;
        if (Math.abs(this.vel) <= Math.max(targetAbs, STOP_EPS)) {
          if (this.autoDriveActive()) {
            this.startBlend(now); // auf Basis-Tempo abgefallen → überblenden
          } else {
            this.vel = 0;
            this.phase = 'auto'; // pausiert: stehen bleiben, Loop endet unten
          }
        }
      } else if (this.phase === 'blend') {
        if (!this.autoDriveActive()) {
          this.phase = 'decay'; // mitten in der Überblendung pausiert → ausrollen
        } else {
          const t = Math.min((now - this.blendStart) / BLEND_MS, 1);
          const eased = t * (2 - t); // easeOutQuad — weicher Übergang ohne Knick
          this.vel = this.blendFrom + (this.baseVel() - this.blendFrom) * eased;
          this.pos = this.wrapPos(this.pos + this.vel * dt);
          if (t >= 1) this.phase = 'auto';
        }
      } else if (this.autoDriveActive()) {
        this.vel = this.baseVel();
        this.pos = this.wrapPos(this.pos + this.vel * dt);
      } else {
        this.vel = 0;
      }

      this.applyPos();
      if (this.needsFrames()) {
        this.engineRafId = requestAnimationFrame((n) => this.engineTick(n));
      } else {
        this.lastFrameTime = 0;
      }
    }

    // ── Pause-Control (WCAG 2.2.2: bewegte Inhalte müssen pausierbar sein) ───

    addPauseControl(el) {
      const { signal } = this.controller;
      const meta = { el, addedRole: false, addedTabindex: false, addedLabel: false, addedType: false };
      el.classList.add('fpu-marquee-pause-control');
      const nativeButton = el.tagName === 'BUTTON';
      if (nativeButton) {
        if (!el.hasAttribute('type')) { el.setAttribute('type', 'button'); meta.addedType = true; }
      } else {
        if (!el.hasAttribute('role')) { el.setAttribute('role', 'button'); meta.addedRole = true; }
        if (!el.hasAttribute('tabindex')) { el.setAttribute('tabindex', '0'); meta.addedTabindex = true; }
        // Enter/Space wie bei einem echten Button behandeln
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            this.togglePause();
          }
        }, { signal });
      }
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby') && !el.textContent.trim()) {
        el.setAttribute('aria-label', 'Laufband pausieren');
        meta.addedLabel = true;
      }
      el.setAttribute('aria-pressed', String(this.paused));
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.togglePause();
      }, { signal });
      this.pauseControls.push(meta);
    }

    togglePause(force) {
      this.paused = typeof force === 'boolean' ? force : !this.paused;
      this.wrapper.classList.toggle('fpu-marquee-paused', this.paused);
      for (const { el } of this.pauseControls) el.setAttribute('aria-pressed', String(this.paused));
      // Drag-Modus: beim Fortsetzen die rAF-Engine wieder anwerfen
      // (beim Pausieren beendet sich der Loop selbst über needsFrames)
      if (this.drag) this.startEngine();
    }

    // ── Aufräumen ────────────────────────────────────────────────────────────

    destroy() {
      this.controller.abort();
      this.resizeObserver.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);

      // Drag-Engine: Loop stoppen, laufenden Drag lösen, Beobachter trennen
      if (this.engineRafId) cancelAnimationFrame(this.engineRafId);
      if (this.dragController) this.dragController.abort();
      if (this.viewObserver) this.viewObserver.disconnect();
      // Nur im Drag-Modus zurücksetzen — im CSS-Modus hat das Script den
      // Inline-Transform nie gesetzt (Autoren-Styles bleiben unangetastet).
      if (this.drag) this.track.style.removeProperty('transform');

      // Klone entfernen, Original-Knoten aus der Gruppe zurück in den Track
      for (const clone of this.groups.slice(1)) clone.remove();
      const group = this.groups[0];
      group.replaceWith(...group.childNodes);
      this.groups = [];

      this.track.classList.remove('fpu-marquee-track', 'fpu-marquee-track-x', 'fpu-marquee-track-y', 'fpu-marquee-track-reverse', 'fpu-marquee-ready', 'fpu-marquee-drag-mode');
      this.wrapper.classList.remove('fpu-marquee-wrapper', 'fpu-marquee-horizontal', 'fpu-marquee-vertical', 'fpu-marquee-paused', 'fpu-marquee-hover-pause', 'fpu-marquee-fade', 'fpu-marquee-drag', 'fpu-marquee-dragging');
      for (const prop of ['--fpu-marquee-distance', '--fpu-marquee-duration', '--fpu-marquee-gap', '--fpu-marquee-fade-size']) {
        this.wrapper.style.removeProperty(prop);
      }

      for (const meta of this.pauseControls) {
        meta.el.classList.remove('fpu-marquee-pause-control');
        meta.el.removeAttribute('aria-pressed');
        if (meta.addedRole) meta.el.removeAttribute('role');
        if (meta.addedTabindex) meta.el.removeAttribute('tabindex');
        if (meta.addedLabel) meta.el.removeAttribute('aria-label');
        if (meta.addedType) meta.el.removeAttribute('type');
      }
      this.pauseControls.length = 0;
    }
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  let initialized = false;
  function initAll() {
    if (initialized) return;
    initialized = true;
    GLOBAL = window.fpuMarqueeConfig || {};
    injectStyles();

    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper, i) => {
      try {
        // Eigenen Track finden — nicht den eines verschachtelten Marquees
        const track = Array.from(wrapper.querySelectorAll(TRACK_SELECTOR))
          .find((t) => t.closest(WRAPPER_SELECTOR) === wrapper);
        if (!track) {
          console.warn(`${LOG_PREFIX} Marquee #${i}: element="track" fehlt — übersprungen.`);
          return;
        }
        if (!track.children.length) {
          console.warn(`${LOG_PREFIX} Marquee #${i}: Track hat keinen Inhalt — übersprungen.`);
          return;
        }
        instances.push(new FpuMarquee(wrapper, track, i));
      } catch (err) {
        console.error(`${LOG_PREFIX} Init fehlgeschlagen:`, err);
      }
    });

    // Pause-Controls verdrahten: im Wrapper liegend, via -instance verknüpft
    // oder eindeutig (genau eine Instanz auf der Seite).
    for (const el of document.querySelectorAll(PAUSE_SELECTOR)) {
      try {
        const owner = el.closest(WRAPPER_SELECTOR);
        let instance = null;
        if (owner) {
          instance = instances.find((m) => m.wrapper === owner);
        } else {
          const key = el.getAttribute('fpu-marquee-instance');
          if (key !== null) {
            instance = instances.find((m) => m.instanceKey === key);
          } else if (instances.length === 1) {
            instance = instances[0];
          }
        }
        if (instance) {
          instance.addPauseControl(el);
        } else {
          console.warn(`${LOG_PREFIX} pause-Element ohne zugehörigen Wrapper — bitte in den Wrapper legen oder per fpu-marquee-instance verknüpfen.`, el);
        }
      } catch (err) {
        console.error(`${LOG_PREFIX} Pause-Control fehlgeschlagen:`, err);
      }
    }

    // Beim gemeinsamen Dach melden — daraufhin laufen die Rueckrufe, die
    // jemand ueber window.FlowPowerUser.push() angemeldet hat. Defensiv
    // aufgerufen: ohne Kern (Selbsttest) passiert hier einfach nichts.
    window.__fpu?.anmelden?.('marquee', {
      version: VERSION,
      api: window.fpuMarquee,
      starten: initAll,
      abraeumen: () => window.fpuMarquee.destroyAll(),
    });
  }

  window.fpuMarquee = {
    version: VERSION,
    instances,
    init: initAll,
    // Fuer den Selbsttest: die Bausteine, die sich ohne Browser pruefen
    // lassen. Die Options-Helfer lesen nur `this.wrapper`, laufen also mit
    // einer Attrappe als `this`.
    __pure: { FpuMarquee, DIRECTIONS, DEFAULT_SPEED, DEFAULT_FADE },
    destroyAll() {
      for (const instance of instances) instance.destroy();
      instances.length = 0;
      initialized = false;
    },
  };

  // Primärpfad: Webflow-Queue. webflow.js adoptiert ein vorab existierendes
  // Array und führt die Queue nach dem Build seiner Module aus — funktioniert
  // daher auch bei Script-Platzierung im <head>.
  (window.Webflow = window.Webflow || []).push(initAll);

  // Fallback nur für Seiten ohne webflow.js: dort bleibt window.Webflow ein
  // gewöhnliches Array und kein webflow.js-Script ist im Dokument.
  const initWithoutWebflow = () => {
    if (initialized) return;
    const hasWebflowScript = !!document.querySelector('script[src*="webflow"]');
    if (!hasWebflowScript && Array.isArray(window.Webflow)) initAll();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithoutWebflow, { once: true });
  } else {
    setTimeout(initWithoutWebflow, 0);
  }
  // Sicherheitsnetz: Queue wurde bis zum Load-Event nie adoptiert → selbst starten.
  window.addEventListener('load', () => {
    if (!initialized && Array.isArray(window.Webflow)) initAll();
  }, { once: true });
})();
